import * as React from 'react'
import { useMemo, useState } from 'react'
import { AccountDto } from '@halo/dtos/account.dto'
import { BillingDataResponse } from '@pushly/models/lib/structs/billing'
import { Button, Well, Drawer } from '@pushly/aqe/lib/components'
import { EditOutlined, WarningFilled } from '@ant-design/icons'
import { Form, InputNumber, notification, Select, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { simpleNotification } from '@halo/_utils/notifications'
import { AccountService } from '@halo/services/account.service'
import CopyToClipboard from 'react-copy-to-clipboard'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT, SUPPORTED_BILLING_CURRENCIES } from '@halo/constants'
import * as randomstring from 'randomstring'
import './style/account-billing-well.scss'

interface IAccountBillingEditor {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IAccountBillingFormValues {
    type: string
    currency: string
    rate_type: string
    rate: number
}

export const AccountBillingEditor: React.FC<IAccountBillingEditor> = (props) => {
    const accountService = useMemo(() => Container.get(AccountService), [])

    const [form] = Form.useForm<IAccountBillingFormValues>()

    const propsValue: BillingDataResponse = props.account.billing_data || new BillingDataResponse()

    const initialValues = useMemo<IAccountBillingFormValues>(
        () => ({
            type: (propsValue.type || 'INVOICE').toUpperCase(),
            currency: (propsValue.currency || 'USD').toUpperCase(),
            rate_type: (propsValue.rate_type || 'FLAT_RATE').toUpperCase(),
            rate: propsValue.rate,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.account],
    )

    const watchedCurrency = Form.useWatch('currency', form)
    const currentCurrency = (watchedCurrency || initialValues.currency) as string
    const currentCurrencySymbol =
        SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === currentCurrency)?.symbol ?? ''

    const handleSubmit = async (values: IAccountBillingFormValues) => {
        try {
            const mergedValues = {
                ...initialValues,
                ...values,
            }

            const { ok, data } = await accountService.patchBillingData(props.account.id, mergedValues, true)

            if (ok) {
                props.onAccountUpdated({
                    billing_data: data,
                })

                props.onClose(null)
                simpleNotification('success', `Organization, ${props.account.name}, has been successfully updated.`)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    const handleDrawerSubmit = async () => {
        try {
            const values = await form.validateFields()
            await handleSubmit(values)
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            className={classnames('adf-billing-editor')}
            title="Update Billing"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleDrawerSubmit}
            onClose={props.onClose}
            open={props.visible}
        >
            <Form
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                form={form}
                initialValues={initialValues}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well title="Billing Configuration" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item label="Billing Type" name="type">
                        <Select>
                            <Select.Option value="INVOICE">Invoicing</Select.Option>
                            <Select.Option value="CREDIT">Credit Card</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Currency" name="currency">
                        <Select showSearch={false}>
                            {SUPPORTED_BILLING_CURRENCIES.map((cur) => (
                                <Select.Option key={cur.value} value={cur.value}>
                                    {cur.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Rate Type" name="rate_type">
                        <Select>
                            <Select.Option value="FLAT_RATE">Flat Monthly Rate</Select.Option>
                            <Select.Option value="VARIABLE_SUBSCRIBER">Variable Subscriber Rate</Select.Option>
                            <Select.Option value="REVSHARE-PV">Revshare - Page Views</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Rate Amount"
                        name="rate"
                        rules={[
                            {
                                required: true,
                                message: 'Rate Amount is required.',
                            },
                        ]}
                    >
                        <InputNumber
                            min={0}
                            precision={2}
                            step={1}
                            decimalSeparator={'.'}
                            formatter={(value) =>
                                `${currentCurrencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                        />
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IAccountBillingWell {
    account: AccountDto
    onAccountUpdated: (account: Partial<AccountDto>) => void
    loading?: boolean
    title?: string
}

interface IABWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountBillingWell extends React.Component<IAccountBillingWell, IABWState> {
    public state: IABWState = {
        showDrawer: false,
    }

    protected appService: AppService

    public constructor(props: IAccountBillingWell) {
        super(props)

        this.appService = Container.get(AppService)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Billing Configuration"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderBillingInformation()}
                            {this.renderStripeIntegration()}

                            <AccountBillingEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.props.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderBillingInformation() {
        return (
            <Well.Section title="Billing Configuration">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Billing Type</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.currentTypeIsCredit ? 'Credit Card' : 'Invoicing'}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Currency</div>
                    <div className={classnames('form-layout-row-value')}>{this.currentCurrencyDisplayName}</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Rate Type</div>
                    <div className={classnames('form-layout-row-value')}>{this.normalizeRateType()}</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Rate Amount</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.currentCurrencySymbol}
                        {this.currentValues.rate}
                        {this.currentRateTypeIsPageView
                            ? ' CPM'
                            : this.currentRateTypeIsFlat
                            ? ' / month'
                            : ' / subscriber'}
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected renderStripeIntegration() {
        return (
            <Well.Section
                title={
                    <>
                        <span>Stripe Integration </span>
                        {!!this.currentValues.stripe_customer_id && (
                            <span className="lowlight">(Client Configured)</span>
                        )}
                    </>
                }
            >
                {!this.currentValues.stripe_customer_id ? (
                    <>
                        <div className="billing-no-card-config">
                            <WarningFilled />
                            <span>Awaiting client card configuration</span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Customer Reference ID</div>
                            <div className={classnames('form-layout-row-value', 'can-copy')}>
                                <CopyToClipboard
                                    onCopy={() => this.handleCopied('Customer reference ID')}
                                    text={this.currentValues.stripe_customer_id}
                                >
                                    <span>{this.currentValues.stripe_customer_id}</span>
                                </CopyToClipboard>
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Contact Email</div>
                            <div className={classnames('form-layout-row-value')}>
                                {this.currentValues.stripe_customer_email}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Current Card</div>
                            <div className={classnames('form-layout-row-value')}>
                                **** **** **** {this.currentValues.stripe_customer_last4}
                            </div>
                        </div>
                    </>
                )}
            </Well.Section>
        )
    }

    protected get propsValue(): any {
        return this.props.account.billing_data || {}
    }

    protected get currentValues() {
        const propsValue = this.propsValue

        let values: any = {
            type: (propsValue.type || 'INVOICE').toUpperCase(),
            currency: (propsValue.currency || 'USD').toUpperCase(),
            rate_type: (propsValue.rate_type || 'FLAT_RATE').toUpperCase(),
            rate: propsValue.rate,
        }

        if (propsValue.stripe_customer_id) {
            values.stripe_customer_id = propsValue.stripe_customer_id
            values.stripe_customer_email = propsValue.stripe_customer_email
            values.stripe_customer_last4 = propsValue.stripe_customer_last4
        }

        return values
    }

    protected get currentTypeIsCredit(): boolean {
        return this.currentValues.type === 'CREDIT'
    }

    protected get currentRateTypeIsFlat(): boolean {
        return this.currentValues.rate_type === 'FLAT_RATE'
    }

    protected get currentRateTypeIsPageView(): boolean {
        return this.currentValues.rate_type === 'REVSHARE-PV'
    }

    protected normalizeRateType(): string {
        const rate_type = this.currentValues.rate_type
        return rate_type === 'FLAT_RATE'
            ? 'Flat Monthly Rate'
            : rate_type === 'VARIABLE_SUBSCRIBER'
            ? 'Variable Subscriber Rate'
            : 'Revshare - Page Views'
    }

    protected get currentCurrencyDisplayName(): string {
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === this.currentValues.currency)!.label
    }

    protected get currentCurrencySymbol(): string {
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === this.currentValues.currency)!.symbol
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
